/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { colors } from "@farmlands/mui-theme";
import { Box, Typography } from "@mui/material";
import { FC, useCallback, useState } from "react";

import Autocomplete from "../../../../components/Autocomplete";
import { LookupValue } from "../../api/types/types";

import { competitorFilterOptions, highlightMatchingText } from "./filterUtils";

export interface CompetitorAutocompleteProps {
  value: LookupValue | null;
  options: LookupValue[];
  disabled: boolean;
  onChange: (value: LookupValue | null) => void;
  error?: boolean;
}

const CompetitorAutocomplete: FC<CompetitorAutocompleteProps> = ({
  value,
  options,
  disabled,
  onChange,
  error = false,
}) => {
  const [inputValue, setInputValue] = useState("");

  const renderOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option: LookupValue) => (
      <li {...props}>{highlightMatchingText(option.label || "", inputValue.trim())}</li>
    ),
    [inputValue]
  );

  const isOptionEqualToValue = useCallback((option: LookupValue, value: LookupValue) => {
    if (!option || !value) {
      return false;
    }
    if (option.id && value.id) {
      return option.id === value.id;
    }
    if (option.value !== undefined && value.value !== undefined) {
      return option.value === value.value;
    }
    return false;
  }, []);

  return (
    <Box
      sx={{
        width: { xs: "100%", sm: "calc(50% - 0.5rem)" },
        position: "relative",
        overflow: "visible",
      }}
    >
      <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
        Competitor
      </Typography>
      <Autocomplete<LookupValue>
        disabled={disabled}
        isTopElement
        options={options}
        value={value}
        onChange={(_, newValue) => onChange(newValue)}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        getOptionLabel={(option) => option.label || ""}
        filterOptions={competitorFilterOptions}
        renderOption={renderOption}
        isOptionEqualToValue={isOptionEqualToValue}
        placeholder="Select competitor"
        ListboxProps={{
          sx: {
            maxHeight: "12.5rem",
          },
        }}
        componentsProps={{
          popper: {
            sx: { zIndex: 10000 },
            modifiers: [
              {
                name: "preventOverflow",
                options: {
                  boundary: "clippingParents",
                  rootBoundary: "viewport",
                  padding: 8,
                  altBoundary: true,
                },
              },
              {
                name: "flip",
                options: {
                  fallbackPlacements: ["top", "bottom"],
                  boundary: "clippingParents",
                },
              },
            ],
          },
          paper: {
            sx: {
              maxHeight: "12.5rem",
              overflow: "auto",
            },
          },
        }}
        sx={{
          width: "100%",
          "& .MuiInputBase-root.Mui-disabled": {
            backgroundColor: colors.primary50,
          },
          "&.Mui-disabled .MuiInputBase-root": {
            backgroundColor: colors.primary50,
          },
          "& .MuiOutlinedInput-root.Mui-disabled": {
            backgroundColor: colors.primary50,
          },
        }}
        textFieldProps={{
          error,
          sx: {
            "& .MuiOutlinedInput-root.Mui-disabled": {
              backgroundColor: colors.primary50,
            },
          },
        }}
      />
    </Box>
  );
};

export default CompetitorAutocomplete;
